import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export interface ContractTemplate {
  id: string;
  name: string;
  description: string | null;
  content: string;
  is_default: boolean;
  created_at: string;
}

export function useContractTemplates() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const queryKey = ["contract_templates", user?.id];

  const { data, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<ContractTemplate[]> => {
      if (!user) return [];
      const { data } = await supabase
        .from("contract_templates")
        .select("id, name, description, content, is_default, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(100);
      return (data as ContractTemplate[]) || [];
    },
    enabled: !!user,
    staleTime: 15 * 60_000,
  });

  const templates = data ?? [];

  const saveTemplate = async (template: Partial<ContractTemplate> & { name: string; content: string }) => {
    if (!user) return;
    if (template.id) {
      const { error } = await supabase.from("contract_templates").update({
        name: template.name,
        description: template.description,
        content: template.content,
        is_default: template.is_default || false,
      }).eq("id", template.id);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    } else {
      const { error } = await supabase.from("contract_templates").insert({
        user_id: user.id,
        name: template.name,
        description: template.description || null,
        content: template.content,
        is_default: template.is_default || false,
      });
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    }
    toast({ title: "Template saved" });
    await qc.invalidateQueries({ queryKey });
  };

  const deleteTemplate = async (id: string) => {
    const { error } = await supabase.from("contract_templates").delete().eq("id", id);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    toast({ title: "Template deleted" });
    await qc.invalidateQueries({ queryKey });
  };

  return { templates, isLoading, saveTemplate, deleteTemplate, refetch };
}
